// src/components/ObligationsPanel.js
//
// THE OBLIGATION OF MEMBERSHIP, ON A SCREEN (plan Part A; rulings 207-208; task list section 349).
//
// Section 346 built the record, the rules and the callable; nothing rendered them, so an officer had no way
// to file one. This is that list and that form, and it is deliberately the plainest thing on the platform:
// a date, which meeting, who administered it, and the signed copy.
//
// WHAT IT IS NOT. It is not the ritual - the Constitution's words belong to the local union's meeting, not
// to a screen. It is not a status the member is judged by: ruling 208.3's flag (OBLIGATION_FLAG_DAYS in
// src/data/obligationRecord.js - the number lives there and nowhere else) is a flag on the
// LOCAL's duty to convene, and the panel says so where the flag appears, so nobody reads it as the member's
// failure to attend.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { MEETING_TYPES, MEETING_LABELS, obligationOverdue, OBLIGATION_FLAG_DAYS } from '../data/obligationRecord';
import { capScopes } from '../data/scopeCap';   // T5 item 20: the 30-territory cap, in one place

const GOLD = '#C9A84C';
const GREEN = '#40C070';

export default function ObligationsPanel({ user }) {
  const [records, setRecords] = useState([]);      // onboardingRecords: who was approved, and into which Local
  const [obligations, setObligations] = useState({});
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ obligationDate: '', meetingType: 'membership_meeting', presidingOfficer: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  // Forensic audit 2026-10-16: each read's failure, apart from `error` (the filing's). Both used to land in `error`,
  // which the panel never showed when the onboarding read failed (it returned null on no records), and a failed read of
  // the obligations left every member "outstanding" with "Record the obligation" live - a second filing for a member
  // who may have taken it.
  const [recordsErr, setRecordsErr] = useState('');
  const [obligationsErr, setObligationsErr] = useState('');

  // SHAPED TO WHAT THE RULES CAN PROVE (section 364, R2's twin). Both of these were unconstrained
  // collection listeners, and both read rules scope on scopeKey / generalCommittee - so Firestore
  // refused the listing for every officer who has a desk. The GC asks for his committee; the LC asks
  // once per territory; the rows merge here.
  const scopedQueries = React.useCallback((name) => {
    const ref = collection(db, name);
    if (user && (user.isAdmin || user.role === 'admin')) return [query(ref)];
    if (user && user.role === 'general_chair' && user.committee) {
      return [query(ref, where('generalCommittee', '==', user.committee))];
    }
    if (user && user.role === 'local_chair' && Array.isArray(user.scopes) && user.scopes.length) {
      return capScopes(user).scopes.map((sk) => query(ref, where('scopeKey', '==', sk)));
    }
    return [];
  }, [user]);

  useEffect(() => {
    const qs = scopedQueries('onboardingRecords');
    if (!qs.length) { setRecords([]); return undefined; }
    const buf = qs.map(() => []);
    const merge = () => {
      const seen = new Map();
      buf.forEach((rows) => rows.forEach((r) => seen.set(r.id, r)));
      setRecords([...seen.values()].filter((r) => r.decision === 'approved' && r.memberUid));
    };
    setRecordsErr('');
    const unsubs = qs.map((q, i) => onSnapshot(q,
      (snap) => { buf[i] = snap.docs.map((d) => ({ id: d.id, ...d.data() })); merge(); },
      (e) => setRecordsErr('Could not read the onboarding records (' + (e.code || e.message || 'refused') + '). The members approved into your Locals are unknown until they can be read - this is NOT "nobody to obligate".')));
    return () => unsubs.forEach((u) => u());
  }, [scopedQueries]);

  useEffect(() => {
    const qs = scopedQueries('obligations');
    if (!qs.length) { setObligations({}); return undefined; }
    const buf = qs.map(() => []);
    const merge = () => {
      const by = {};
      buf.forEach((rows) => rows.forEach((v) => { if (v && v.memberUid) by[v.memberUid] = v; }));
      setObligations(by);
    };
    setObligationsErr('');
    const unsubs = qs.map((q, i) => onSnapshot(q,
      (snap) => { buf[i] = snap.docs.map((d) => ({ id: d.id, ...d.data() })); merge(); },
      // A REFUSED READ IS NOT "NO OBLIGATION ON RECORD". The handler here used to say "nothing to
      // read is not an error" and swallow the refusal - which would tell an officer that a member
      // who HAS taken the obligation has not.
      (e) => setObligationsErr('Could not read the obligations on record (' + (e.code || e.message || 'refused') + '). Whether each member below has taken it is unknown, so none is shown as outstanding and none can be recorded until it can be read.')));
    return () => unsubs.forEach((u) => u());
  }, [scopedQueries]);

  const rows = useMemo(() => records.map((r) => {
    const obligation = obligations[r.memberUid] || null;
    const approvedMs = (r.decidedAt && r.decidedAt.seconds ? r.decidedAt.seconds * 1000 : 0) || 0;
    const od = obligationOverdue(approvedMs, obligation ? [obligation] : [], Date.now());
    return { record: r, obligation, overdue: od.overdue, days: od.days };
  }).sort((a, b) => (a.obligation ? 1 : 0) - (b.obligation ? 1 : 0) || (b.days || 0) - (a.days || 0)), [records, obligations]);

  const fileIt = async (row) => {
    setError(''); setNote('');
    const r = row.record;
    if (!file) { setError('The signed obligation is part of the act - attach the signed copy.'); return; }
    setBusy(true);
    try {
      // Into THAT Local's own records, tagged as storage.rules requires: who filed it, which committee,
      // which Local. An untagged or mis-tagged object is refused by the rules, not by this screen.
      const safe = String(file.name).replace(/[^A-Za-z0-9._-]/g, '_');
      const path = 'locals/' + r.scopeKey + '/obligation-' + r.memberUid + '-' + Date.now() + '-' + safe;
      await uploadBytes(ref(storage, path), file, {
        customMetadata: { createdBy: user?.uid || '', committee: r.generalCommittee || '', scopeKey: r.scopeKey || '', memberUid: r.memberUid },
      });
      const fn = httpsCallable(getFunctions(), 'recordObligation');
      await fn({ form: { ...form, memberUid: r.memberUid, scopeKey: r.scopeKey, generalCommittee: r.generalCommittee, signedCopyPath: path } });
      setNote('Filed. The signed copy is in that Local’s records, and his General Chairperson has it too. [R-208.2]');
      setOpen(null); setFile(null);
      setForm({ obligationDate: '', meetingType: 'membership_meeting', presidingOfficer: '' });
    } catch (e) {
      setError((e && e.message) || 'That could not be filed.');
    } finally {
      setBusy(false);
    }
  };

  if (!records.length && !recordsErr) return null;   // forensic audit 2026-10-16: a failed read is shown, never hidden

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <h3 style={{ fontSize: 14, margin: '0 0 4px' }}>Obligations of membership</h3>
      <div style={{ fontSize: 11.5, color: '#8AA4BF', marginBottom: 10 }}>
        Every approved member takes the obligation at a meeting of the local union. It is the local&#39;s duty
        to convene it, not the member&#39;s to chase - a flag here after {OBLIGATION_FLAG_DAYS} days is about
        the local. [R-208]
      </div>
      {recordsErr && <div className="alert-banner">&#9888; {recordsErr}</div>}
      {obligationsErr && <div className="alert-banner">&#9888; {obligationsErr}</div>}
      {error && <div className="alert-banner">&#9888; {error}</div>}
      {note && <div className="alert-banner" style={{ borderColor: GREEN, color: GREEN }}>{note}</div>}

      {rows.map(({ record, obligation, overdue, days }) => (
        <div key={record.id} style={{ borderTop: '1px solid rgba(138,164,191,0.18)', padding: '10px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
            <div style={{ fontSize: 13 }}>
              {record.fullLegalName || record.memberUid}
              <span style={{ color: '#6F8AA6', fontSize: 11, marginLeft: 8 }}>
                {record.scopeKey || 'no Local'} - {record.generalCommittee || 'no committee'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: obligation ? GREEN : (obligationsErr ? '#E06C6C' : (overdue ? GOLD : '#8AA4BF')) }}>
              {obligation
                ? 'taken ' + obligation.obligationDate
                : obligationsErr ? 'unknown - the obligations could not be read'
                : (overdue ? 'OUTSTANDING ' + days + ' days - the local has not convened it' : 'outstanding')}
            </div>
          </div>

          {!obligation && !obligationsErr && (
            <div style={{ marginTop: 8 }}>
              {open === record.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 520 }}>
                  <input aria-label="Obligation date" className="form-input" type="date" value={form.obligationDate}
                    onChange={(e) => setForm((f) => ({ ...f, obligationDate: e.target.value }))} />
                  <select aria-label="Meeting type" className="form-input" value={form.meetingType}
                    onChange={(e) => setForm((f) => ({ ...f, meetingType: e.target.value }))}>
                    {MEETING_TYPES.map((t) => <option key={t} value={t}>{MEETING_LABELS[t]}</option>)}
                  </select>
                  <input aria-label="President or presiding officer, by name" className="form-input" placeholder="President or presiding officer, by name"
                    value={form.presidingOfficer} onChange={(e) => setForm((f) => ({ ...f, presidingOfficer: e.target.value }))} />
                  <input aria-label="Attach the document for this obligation" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0] || null)} style={{ color: '#F4F6F8', fontSize: 12 }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12 }} disabled={busy} onClick={() => fileIt({ record })}>
                      {busy ? 'Filing...' : 'File the obligation'}
                    </button>
                    <button className="btn btn-secondary" style={{ fontSize: 12 }} disabled={busy} onClick={() => { setOpen(null); setFile(null); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setOpen(record.id)}>
                  Record the obligation
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
